import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface ResultsPaginationProps {
  currentPage: number;
  totalResults: number; // Total de resultados no CHUNK atual
  resultsPerPage: number;
  onPageChange: (page: number) => void;
  hasMoreChunks: boolean;
}

const ResultsPagination: React.FC<ResultsPaginationProps> = ({ 
  currentPage, 
  totalResults, 
  resultsPerPage, 
  onPageChange,
  hasMoreChunks
}) => {
  const { t } = useTranslation();
  // totalPages agora é o total de páginas DENTRO do chunk
  const totalDisplayPages = Math.ceil(totalResults / resultsPerPage);

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    // Se houver mais páginas de exibição no chunk atual, ou se for a última página do chunk mas houver mais chunks
    if (currentPage < totalDisplayPages || (currentPage === totalDisplayPages && hasMoreChunks)) {
      onPageChange(currentPage + 1);
    }
  };

  const startResult = totalResults === 0 ? 0 : (currentPage - 1) * resultsPerPage + 1;
  const endResult = Math.min(currentPage * resultsPerPage, totalResults);
  
  if (totalResults === 0) return null;

  // Desabilitado se estiver na última página de exibição E não houver mais chunks
  const isNextDisabled = currentPage === totalDisplayPages && !hasMoreChunks;

  return (
    <motion.div 
      className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700/50"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
    >
      <motion.p 
        className="text-sm text-gray-600 dark:text-gray-400"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
      >
        {t('dashboard.pagination_showing', { start: startResult, end: endResult, total: totalResults })}
      </motion.p>
      
      <motion.div 
        className="flex items-center gap-2"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.8 }}
      >
        <motion.button
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className="flex items-center justify-center rounded-lg h-9 w-9 bg-gray-100 dark:bg-gray-900/50 hover:bg-gray-200 dark:hover:bg-gray-700/50 text-gray-600 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          whileHover={{ scale: currentPage === 1 ? 1 : 1.05 }}
          whileTap={{ scale: currentPage === 1 ? 1 : 0.95 }}
        >
          <span className="material-symbols-outlined text-lg">chevron_left</span>
        </motion.button>
        
        <p className="text-sm font-medium text-[#181411] dark:text-white">
            {t('dashboard.pagination_page', { current: currentPage, total: totalDisplayPages })}
        </p>
        
        <motion.button
          onClick={handleNext}
          disabled={isNextDisabled}
          className="flex items-center justify-center rounded-lg h-9 w-9 bg-gray-100 dark:bg-gray-900/50 hover:bg-gray-200 dark:hover:bg-gray-700/50 text-gray-600 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          whileHover={{ scale: isNextDisabled ? 1 : 1.05 }}
          whileTap={{ scale: isNextDisabled ? 1 : 0.95 }}
        >
          <span className="material-symbols-outlined text-lg">chevron_right</span>
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default ResultsPagination;